import { DateTime } from 'luxon'
import ExcelJS from 'exceljs'
import { LimitPeriod } from '@types'
import unidecode from 'unidecode'
import API from '@enums/api.ts'

/**
 * Normalize and sanitize a string by converting it to ASCII and removing special characters
 * @param input The input string to normalize and sanitize
 * @returns The normalized and sanitized string
 */
export function normalizeAndSanitize(input: string): string {
    const transliterated = unidecode(input.normalize('NFKC')) // Convert to ASCII
    return transliterated.replace(/[^a-zA-Z0-9 ]/g, '')
}

/**
 * Capitalize the first character in a string
 */
export function capitalize(str: string) {
    return str[0].toUpperCase() + str.slice(1)
}

/**
 * Capitalize all words in a string separated by spaces
 */
export function capitalizeAll(str: string) {
    return str
        .split(' ')
        .map((bit) => capitalize(bit))
        .join(' ')
}

/**
 * Helper function to paginate row data
 */
export function paginate<T>(rows: T[], page: number, rowsPerPage: number): T[] {
    if (rowsPerPage === -1) {
        // "show all" is selected
        return rows
    }

    return rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
}

type DownloadFileParams = {
    /** URL to file download. */
    fileURL: string
    /** Filename to save as. For example, "example.csv". */
    saveAsFileName: string
    /** Mimetype of download. For example, "text/csv". */
    acceptType: string
}

/**
 * Helper function to download a file from a URL.
 */
export async function downloadFile(params: DownloadFileParams) {
    // Note: I'm as surprised as you are that you have to go through this
    // rickmaroll just to download a file using javascript :shrug:
    const res = await API.download.fetchFile(params.fileURL, params.acceptType)
    if (!res.ok) {
        console.error(res)
        throw Error('Failed to download report')
    }
    const blob = res.value
    await downloadBlob(blob, params.saveAsFileName)
}

export async function downloadBlob(blob: Blob, desiredFilename: string) {
    const aElement = document.createElement('a')
    aElement.setAttribute('download', desiredFilename)
    const href = URL.createObjectURL(blob)
    aElement.href = href
    aElement.setAttribute('target', '_blank')
    aElement.click()
    URL.revokeObjectURL(href)
}

export const csvToExcel = async (csvData: string) => {
    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Users')

    // Parse the CSV string into rows and cells
    const rows = csvData.split('\n').map((row) => row.split(','))

    // Add rows to the worksheet
    rows.forEach((row) => {
        worksheet.addRow(row)
    })

    worksheet.columns = worksheet.columns.map((col) => {
        return {
            ...col,
            width: 30,
        }
    })

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    return blob
}

export function kebabCaseToPascalCase(str: string) {
    return str
        .split('-')
        .map((word) => {
            const firstLetter = word.slice(0, 1)
            const rest = word.slice(1)
            return firstLetter.toUpperCase() + rest
        })
        .join(' ')
}

export function snakeCaseToPascalCase(str: string) {
    return str
        .split('_')
        .map((word) => {
            const firstLetter = word.slice(0, 1)
            const rest = word.slice(1)
            return firstLetter.toUpperCase() + rest
        })
        .join(' ')
}

export function toInitials(str: string) {
    return str
        .replace(/[^a-zA-Z0-9 ]/g, '') // remove any special chars
        .split(' ')
        .map((word) => {
            return word.slice(0, 1).toUpperCase()
        })
        .join('')
}

export function isSelectingText() {
    const selection = window.getSelection()
    return selection?.type === 'Range'
}

export function displayPrice(currency: string, value: number) {
    return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value)
}

/**
 * Localised version of YYYY-MM-DD
 */
export function isoToLocalShortDate(isoDateString: string) {
    const dt = DateTime.fromISO(isoDateString)
    return dt.toLocaleString(DateTime.DATE_SHORT)
}

export const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]
export const years = Array.from(
    { length: 11 },
    (_, i) => new Date().getFullYear() - 5 + i
)

export function getFirstAndLastDayOfRange(
    start: DateTime,
    end: DateTime
): { firstDay: Date; lastDay: Date }
export function getFirstAndLastDayOfRange(
    start: Date,
    end: Date
): { firstDay: Date; lastDay: Date }
export function getFirstAndLastDayOfRange(
    start: Date | DateTime,
    end: Date | DateTime
): { firstDay: Date; lastDay: Date } {
    const startMs = start.valueOf()
    const endMs = end.valueOf()

    const firstDay: Date = new Date(startMs)
    const lastDay: Date = new Date(endMs)

    firstDay.setUTCHours(0, 0, 0, 0)
    lastDay.setUTCHours(23, 59, 59, 999)

    return { firstDay, lastDay }
}

export function getFirstAndLastDayOfMonth(month: string, year?: number) {
    const monthIndex = monthNames.indexOf(month)

    if (monthIndex === -1) {
        throw new Error('Invalid month name')
    }

    if (!year) {
        year = new Date().getFullYear()
    }

    // Create a Date object for the first day of the next month
    const firstDayNextMonth = new Date(year, monthIndex + 1, 1)
    // Subtract one day to get the last day of the current month
    const lastDay = DateTime.fromJSDate(firstDayNextMonth)
        .minus({ days: 1 })
        .toJSDate()
    // Set time to 23:59:59.999
    lastDay.setUTCHours(23, 59, 59, 999)

    const firstDayAsISO = new Date(year, monthIndex, 1).toISOString()
    const lastDayAsISO = lastDay.toISOString()
    return { firstDayAsISO, lastDayAsISO }
}

const months = [
    'undefined',
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
]

interface RangeReturn {
    start_date: DateTime
    end_date: DateTime
}

/**
 * Find the start and end date-times of an invoice period.
 * @param period The period of time after which the limit refreshes
 * @param month The long-form month (i.e. "January") to get the range for
 * @param month_half If the period is "half-month", which half of the month should be checked
 * @param year Which year is the month in?
 * @returns {RangeReturn}
 */
export function getInvoiceRange(
    period: 'full-month',
    month: string,
    month_half?: never,
    year?: number
): RangeReturn
export function getInvoiceRange(
    period: 'half-month',
    month: string,
    month_half: 'first' | 'second' | DateTime,
    year?: number
): RangeReturn
export function getInvoiceRange(
    period: LimitPeriod,
    month: string,
    month_half: 'first' | 'second' | DateTime = DateTime.now(),
    year: number = DateTime.now().year!
): RangeReturn {
    const monthIndex = months.indexOf(month) - 1
    if (monthIndex === -1) {
        throw new Error('Invalid month')
    }

    if (!year) {
        year = new Date().getFullYear()
    }

    const firstOfMonth = DateTime.fromJSDate(
        new Date(year, monthIndex, 1)
    ).startOf('day')

    if (!firstOfMonth.toISO()) {
        throw new Error('Failed to get first of month')
    }

    const lastOfMonth = firstOfMonth.endOf('month').endOf('day')

    if (!lastOfMonth.toISO()) {
        throw new Error('Failed to get last of month')
    }
    const midPoint = DateTime.fromJSDate(new Date(year, monthIndex, 15)).endOf(
        'day'
    )

    if (!midPoint.toISO()) {
        throw new Error('Failed to get midpoint of month')
    }

    switch (period) {
        case 'half-month':
            if (typeof month_half !== 'string') {
                month_half = month_half <= midPoint ? 'first' : 'second'
            }
            switch (month_half) {
                case 'first':
                    return {
                        start_date: firstOfMonth,
                        end_date: midPoint,
                    }
                case 'second':
                    return {
                        start_date: midPoint.plus({ days: 1 }).startOf('day'),
                        end_date: lastOfMonth,
                    }
                default:
                    throw new Error('Invalid month_half')
            }
        default:
            return {
                start_date: firstOfMonth,
                end_date: lastOfMonth,
            }
    }
}

export function formatDateForDatabase(
    date: DateTime<boolean> | undefined | null,
    time_of_day: 'start' | 'end'
) {
    if (time_of_day === 'start')
        return date?.startOf('day').toFormat(`yyyy-LL-dd'T'HH:mm:ss.S'Z'`)
    return date?.endOf('day').toFormat(`yyyy-LL-dd'T'HH:mm:ss.S'Z'`)
}

export const formatUrl = (input: string): string => {
    return input
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/^-+|-+$/g, '')
}

export function toTitle(str: string): string {
    return str
        .split(/\s|_|-/gm)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

/**
 * Factory function for creating callbacks that copy a given value.
 * @param value {string} Value to copy.
 * @returns A function that saves `value` to the system clipboard.
 */
export function copyValue(value: string): () => Promise<void> {
    return () => navigator.clipboard.writeText(value)
}

/**
 * Turns a number of milliseconds into `54:19`, or `1:02:05` once it goes past
 * an hour. Gives back an empty string if it is handed anything else.
 *
 * It counts up the hours, minutes and seconds itself instead of using a Date.
 * Putting a duration into `new Date()` treats the number as a moment in time,
 * and asking that moment for its hours gives you an answer in whatever
 * timezone the person is sitting in. That is how a 54 minute recording ended
 * up showing as 19:54:19 in Canada.
 *
 * Use `humanize-duration` instead for anything measured in hours or days. It
 * gives you "2 days, 3 hours", which suits Podium uptime. This one is for
 * short lengths you read down a column and compare.
 */
export function formatDurationHms(value: unknown): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return ''
    }

    const totalSeconds = Math.floor(value / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (part: number) => String(part).padStart(2, '0')

    return hours > 0
        ? `${hours}:${pad(minutes)}:${pad(seconds)}`
        : `${minutes}:${pad(seconds)}`
}

const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60

/**
 * "3h 12m", or "12m" under the hour, because "0.2 hours" helps nobody.
 *
 * Takes seconds, which is what habitat-admin returns for a cap and for usage.
 * Dividing those by 3600 and printing the result is how the user page came to
 * read "0.10277777777777779/1 Hours" for a student six minutes into a one
 * hour allowance. A recording is an arbitrary length, so the fraction is
 * almost never round and the digits after the first say nothing anyone can
 * act on.
 *
 * Lifted out of FreeRecording, which had worked this out already. Keep the
 * two reading the same way: a person comparing the free hours panel with the
 * notes allowance should not have to translate between them.
 */
export function formatDurationHoursMinutes(value: unknown): string {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
        return ''
    }

    const totalMinutes = Math.round(value / SECONDS_PER_MINUTE)
    const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR)
    const minutes = totalMinutes % MINUTES_PER_HOUR

    if (hours === 0) return `${minutes}m`
    if (minutes === 0) return `${hours}h`
    return `${hours}h ${minutes}m`
}
